/**
 * AdminOfferingsSection - CRUD management for entity offerings / services.
 * Lists all offerings across all entities with filter/search, inline edit, delete.
 * Admins can also add a new offering directly to any entity.
 * @atomic @idempotent
 */
import { memo, useState, useCallback } from 'react'
import { WrenchScrewdriverIcon, PencilSquareIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import { toast } from '../../services/toastService'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import { getInputClasses, labelClasses } from '../../styles/inputStyles'

const TYPE_COLORS = {
  item:    'bg-gray-100 text-gray-600',
  service: 'bg-teal-100 text-teal-700',
  package: 'bg-purple-100 text-purple-700',
}

const EMPTY_FORM = { name: '', description: '', offeringType: 'service', price: '', durationMinutes: '', official: true }

const AdminOfferingsSection = memo(function AdminOfferingsSection({ onBack }) {
  const { execute } = useAsyncOperation()
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [showAdd, setShowAdd] = useState(false)
  const [addEntityId, setAddEntityId] = useState('')
  const [addForm, setAddForm] = useState(EMPTY_FORM)

  const {
    data: offerings, loading, error,
    filter, pagination,
    handleFilterChange, handlePageChange, refetch,
  } = useAdminTableState(
    async (params) => execute(async () => {
      const apiParams = { ...params }
      if (params.filter && params.filter !== 'all') { apiParams.offeringType = params.filter; delete apiParams.filter }
      const res = await adminAPI.getAdminOfferings(apiParams)
      return {
        data: res.data?.offerings || [],
        pagination: {
          page: res.data?.pagination?.page || 1,
          totalPages: res.data?.pagination?.totalPages || 1,
          total: res.data?.pagination?.total || 0,
        },
      }
    }),
    { pageSize: 50 }
  )

  const startEdit = useCallback((o) => {
    setEditingId(o.id)
    setEditForm({
      name: o.name,
      description: o.description || '',
      offeringType: o.offering_type,
      price: o.price != null ? String(o.price) : '',
      durationMinutes: o.duration_minutes != null ? String(o.duration_minutes) : '',
      official: o.official,
    })
  }, [])

  const saveEdit = useCallback(async (o) => {
    await execute(() => adminAPI.adminUpdateOffering(o.entity_id, o.id, {
      name: editForm.name.trim(),
      description: editForm.description.trim() || undefined,
      offeringType: editForm.offeringType,
      price: editForm.price !== '' ? parseFloat(editForm.price) : undefined,
      durationMinutes: editForm.durationMinutes !== '' ? parseInt(editForm.durationMinutes, 10) : undefined,
      official: editForm.official,
    }), {
      onSuccess: () => { toast.success('Offering updated'); setEditingId(null); refetch() },
      onError: () => toast.error('Failed to update'),
    })
  }, [editForm, execute, refetch])

  const handleDelete = useCallback(async (entityId, id, name) => {
    if (!confirm(`Remove "${name}"?`)) return
    await execute(() => adminAPI.adminDeleteOffering(entityId, id), {
      onSuccess: () => { toast.success('Offering removed'); refetch() },
      onError: () => toast.error('Failed to delete'),
    })
  }, [execute, refetch])

  const handleAdd = useCallback(async (e) => {
    e.preventDefault()
    if (!addEntityId.trim()) { toast.error('Entity ID is required'); return }
    if (!addForm.name.trim()) { toast.error('Name is required'); return }
    await execute(() => adminAPI.adminAddOffering(addEntityId.trim(), {
      name: addForm.name.trim(),
      description: addForm.description.trim() || undefined,
      offeringType: addForm.offeringType,
      price: addForm.price !== '' ? parseFloat(addForm.price) : undefined,
      durationMinutes: addForm.durationMinutes !== '' ? parseInt(addForm.durationMinutes, 10) : undefined,
    }), {
      onSuccess: () => { toast.success('Offering added'); setShowAdd(false); setAddForm(EMPTY_FORM); setAddEntityId(''); refetch() },
      onError: () => toast.error('Failed to add'),
    })
  }, [addEntityId, addForm, execute, refetch])

  const filterOptions = [
    { value: 'all', label: 'All types' },
    { value: 'item', label: 'Item' },
    { value: 'service', label: 'Service' },
    { value: 'package', label: 'Package' },
  ]

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-6 h-6 text-teal-500" />
            Offerings &amp; Services
          </h2>
        </div>
        <button
          onClick={() => setShowAdd(v => !v)}
          onTouchEnd={(e) => { e.preventDefault(); setShowAdd(v => !v) }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
        >
          <PlusIcon className="w-4 h-4" /> Add offering
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="px-4 py-4 bg-teal-50 border-b border-teal-100 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2 md:col-span-1">
            <label className={labelClasses}>Entity ID</label>
            <input value={addEntityId} onChange={e => setAddEntityId(e.target.value)} placeholder="UUID" className={getInputClasses(false)} />
          </div>
          <div>
            <label className={labelClasses}>Name *</label>
            <input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} className={getInputClasses(false)} />
          </div>
          <div>
            <label className={labelClasses}>Type</label>
            <select value={addForm.offeringType} onChange={e => setAddForm(p => ({ ...p, offeringType: e.target.value }))} className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2">
              <option value="item">Item</option>
              <option value="service">Service</option>
              <option value="package">Package</option>
            </select>
          </div>
          <div>
            <label className={labelClasses}>Price</label>
            <input type="number" min="0" step="0.01" value={addForm.price} onChange={e => setAddForm(p => ({ ...p, price: e.target.value }))} placeholder="0.00" className={getInputClasses(false)} />
          </div>
          <div>
            <label className={labelClasses}>Duration (min)</label>
            <input type="number" min="1" value={addForm.durationMinutes} onChange={e => setAddForm(p => ({ ...p, durationMinutes: e.target.value }))} placeholder="60" className={getInputClasses(false)} />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className={labelClasses}>Description</label>
            <input value={addForm.description} onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))} className={getInputClasses(false)} />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90">Add</button>
          </div>
        </form>
      )}

      <AdminTableControls
        filter={filter}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        pagination={pagination}
        onPageChange={handlePageChange}
        loading={loading}
      />

      {error && <div className="px-4 py-3 text-sm text-red-600">{String(error)}</div>}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Entity', 'Name', 'Type', 'Price', 'Duration', 'Official', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {offerings.map((o) => {
              const isEditing = editingId === o.id
              return (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-[140px] truncate" title={o.entity_id}>{o.entity_name}</td>

                  {isEditing ? (
                    <>
                      <td className="px-4 py-2">
                        <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="w-full text-sm border border-gray-300 rounded px-2 py-1" />
                      </td>
                      <td className="px-4 py-2">
                        <select value={editForm.offeringType} onChange={e => setEditForm(p => ({ ...p, offeringType: e.target.value }))} className="text-xs border border-gray-300 rounded px-2 py-1">
                          <option value="item">Item</option>
                          <option value="service">Service</option>
                          <option value="package">Package</option>
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" min="0" step="0.01" value={editForm.price} onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))} className="w-20 text-sm border border-gray-300 rounded px-2 py-1" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" min="1" value={editForm.durationMinutes} onChange={e => setEditForm(p => ({ ...p, durationMinutes: e.target.value }))} className="w-16 text-sm border border-gray-300 rounded px-2 py-1" placeholder="min" />
                      </td>
                      <td className="px-4 py-2">
                        <input type="checkbox" checked={editForm.official} onChange={e => setEditForm(p => ({ ...p, official: e.target.checked }))} className="rounded" />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1.5">
                          <button onClick={() => saveEdit(o)} onTouchEnd={() => saveEdit(o)} className="text-green-600 hover:text-green-700" title="Save"><CheckIcon className="w-4 h-4" /></button>
                          <button onClick={() => setEditingId(null)} onTouchEnd={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600" title="Cancel"><XMarkIcon className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{o.name}</div>
                        {o.description && <div className="text-xs text-gray-400 truncate max-w-[200px]">{o.description}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${TYPE_COLORS[o.offering_type] || 'bg-gray-100 text-gray-600'}`}>{o.offering_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{o.price != null ? `$${Number(o.price).toFixed(2)}` : <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{o.duration_minutes ? `${o.duration_minutes} min` : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${o.official ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{o.official ? 'Official' : 'Community'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(o)} onTouchEnd={() => startEdit(o)} title="Edit" className="text-primary hover:text-primary/80"><PencilSquareIcon className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(o.entity_id, o.id, o.name)} onTouchEnd={() => handleDelete(o.entity_id, o.id, o.name)} title="Delete" className="text-red-500 hover:text-red-700"><TrashIcon className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
            {!loading && offerings.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No offerings found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminOfferingsSection
